import { Injectable, inject } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { CasesRepository } from '../../../infrastructure/repositories/cases.repository';
import { DisplayFieldValueEntity } from '../../../domain/entities/display-field-value.entity';
import type { GetCaseFieldValuesForDisplayQuery } from './get-case-field-values-for-display.query';
import type { GetCaseFieldValuesForDisplayResult } from './get-case-field-values-for-display.result';

/**
 * Get Case Field Values For Display Use Case (Readonly Mode)
 * OPTIMIZED: Single API call using fieldLabel from field-values endpoint
 * No need for separate field-definitions call
 */
@Injectable({ providedIn: 'root' })
export class GetCaseFieldValuesForDisplayUseCase {
  private readonly _repository = inject(CasesRepository);

  public execute(query: GetCaseFieldValuesForDisplayQuery): Observable<GetCaseFieldValuesForDisplayResult> {
    const { caseId } = query;

    return this._repository.getCaseFieldValuesForDisplay(caseId).pipe(
      map((fieldValues) => {
        const displayValues = fieldValues
          .filter(fv => fv.value != null && fv.value !== '')
          .sort((a, b) => a.sortOrder - b.sortOrder)
          .map(fv => {
            const entity = new DisplayFieldValueEntity();
            entity.label = fv.fieldLabel;
            entity.value = this._formatFieldValue(fv.value, fv.fieldType, fv.optionsJson);
            return entity;
          });

        return { displayValues };
      }),
      catchError((error) => {
        // Preserve HttpErrorResponse (including status for error handling)
        return throwError(() => error);
      })
    );
  }

  private _formatFieldValue(value: string | null | undefined, fieldType: string, optionsJson?: string | null): string {
    if (!value) {
      return 'N/A';
    }

    const options = this._parseOptions(optionsJson);

    try {
      const parsed = JSON.parse(value);

      if (fieldType === 'date' && typeof parsed === 'string') {
        const dateValue = new Date(parsed);
        if (!isNaN(dateValue.getTime())) {
          return dateValue.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
        }
      }

      if (fieldType === 'time' && typeof parsed === 'string') {
        const timeValue = new Date(parsed);
        if (!isNaN(timeValue.getTime())) {
          return timeValue.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
        }
      }

      if ((fieldType === 'currency' || fieldType === 'number') && typeof parsed === 'number') {
        if (fieldType === 'currency') {
          return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(parsed);
        }
        return String(parsed);
      }

      if (Array.isArray(parsed)) {
        if (options.length > 0) {
          return parsed
            .map((v: unknown) => options.find(o => o.value === v)?.label ?? String(v))
            .join(', ');
        }
        return parsed.join(', ');
      }

      if (typeof parsed === 'boolean') {
        return parsed ? 'Yes' : 'No';
      }

      if (typeof parsed === 'object' && parsed !== null) {
        return JSON.stringify(parsed);
      }

      if (options.length > 0 && typeof parsed === 'string') {
        return options.find(o => o.value === parsed)?.label ?? this._valueToLabel(parsed);
      }

      if ((fieldType === 'select' || fieldType === 'radio' || fieldType === 'radio_buttons') && typeof parsed === 'string') {
        return this._valueToLabel(parsed);
      }

      return String(parsed);
    } catch {
      if (options.length > 0) {
        return options.find(o => o.value === value)?.label ?? this._valueToLabel(value);
      }
      if (fieldType === 'select' || fieldType === 'radio' || fieldType === 'radio_buttons') {
        return this._valueToLabel(value);
      }
      return value;
    }
  }

  private _parseOptions(optionsJson?: string | null): { label: string; value: string }[] {
    if (!optionsJson) return [];
    try {
      const parsed = JSON.parse(optionsJson);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }

  private _valueToLabel(value: string): string {
    return value
      .replace(/[-_]/g, ' ')
      .replace(/\b\w/g, (c) => c.toUpperCase());
  }
}
